import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface Event {
  id: string;
  title: string;
  description: string;
  image_url: string;
  start_date: string;
  end_date: string;
}

export default function EventPopup() {
  const [event, setEvent] = useState<Event | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    checkAndShowPopup();
  }, []);

  const checkAndShowPopup = async () => {
    // 오늘 팝업을 이미 봤는지 확인
    const today = new Date().toDateString();
    const lastShown = localStorage.getItem('eventPopupLastShown');
    
    if (lastShown === today) {
      return;
    }

    try {
      const now = new Date().toISOString();
      
      // 활성화되고 팝업으로 설정된 이벤트 중 현재 진행중인 것 가져오기
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('is_active', true)
        .eq('is_popup', true)
        .lte('start_date', now)
        .gte('end_date', now)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        if (error.code !== 'PGRST116') { // PGRST116 = no rows returned
          console.error('팝업 이벤트 로딩 오류:', error);
        }
        return;
      }

      if (data) {
        setEvent(data);
        setIsVisible(true);
      }
    } catch (error) {
      console.error('팝업 이벤트 확인 오류:', error);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    // 오늘 날짜 저장
    const today = new Date().toDateString();
    localStorage.setItem('eventPopupLastShown', today);
  };

  const handleTodayClose = () => {
    handleClose();
  };

  if (!isVisible || !event) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fadeIn"
        onClick={handleClose}
      />
      
      {/* Popup */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden pointer-events-auto animate-scaleIn"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors duration-200 cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          {/* Event Image */}
          {event.image_url && (
            <div className="relative w-full h-80 overflow-hidden">
              <img
                src={event.image_url}
                alt={event.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>
          )}

          {/* Event Content */}
          <div className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {event.title}
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed whitespace-pre-wrap">
              {event.description}
            </p>
            
            <div className="flex items-center text-sm text-gray-500 mb-6">
              <div className="w-5 h-5 flex items-center justify-center mr-2">
                <i className="ri-calendar-line"></i>
              </div>
              <span>
                {new Date(event.start_date).toLocaleDateString('ko-KR')} ~{' '}
                {new Date(event.end_date).toLocaleDateString('ko-KR')}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  window.REACT_APP_NAVIGATE('/events');
                  handleClose();
                }}
                className="flex-1 bg-teal-500 hover:bg-teal-600 text-white py-3 rounded-lg font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                자세히 보기
              </button>
              <button
                onClick={handleTodayClose}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-lg font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                오늘 하루 보지 않기
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
